import { Marquee } from '@/components/motion';
import { cn } from '@ibas/ui';

/**
 * Text-only accreditor/ranking wordmarks. Compliance rule: EduQua appears here
 * as the plain text "EduQua Certified" only — the EduQua logo mark is reserved
 * for its dedicated accreditation page and must never be reproduced elsewhere.
 */
const ACCREDITORS: string[] = [
  'ACBSP Accredited',
  'QS 5-Star (2026)',
  'EduQua Certified',
  'ISASAHE',
  'IACBE Member',
  'IEAC',
  'Swiss Private School Register',
];

export interface AccreditationMarqueeProps {
  className?: string;
}

/**
 * A restrained, seamless ticker of accreditation and ranking wordmarks —
 * rendered as plain text pill badges, never logos, so the row reads as a
 * quiet institutional procession rather than a logo wall. Motion intent:
 * a slow, unhurried drift (the house Marquee primitive) that says "these
 * credentials are simply a fact of who we are," pausing gently on hover so
 * a curious visitor can read a mark before it moves on.
 *
 * Under reduced motion the Marquee primitive renders the row static and
 * horizontally scrollable — content is never lost, only the drift is.
 */
export function AccreditationMarquee({ className }: AccreditationMarqueeProps) {
  return (
    <section
      aria-labelledby="accreditation-marquee-heading"
      className={cn('bg-background px-6 py-16 sm:px-10 lg:px-16', className)}
    >
      <div className="mx-auto max-w-6xl text-center">
        <p
          id="accreditation-marquee-heading"
          className="font-label text-xs tracking-[0.35em] text-muted-foreground uppercase sm:text-sm"
        >
          Recognised and accredited
        </p>
      </div>

      <div className="mt-10 sm:mt-12">
        <Marquee speed={44} gap="gap-4" pauseOnHover>
          {ACCREDITORS.map((name) => (
            <span
              key={name}
              className={cn(
                'inline-flex shrink-0 items-center rounded-full border border-border px-5 py-2.5',
                'font-label text-xs tracking-[0.18em] text-muted-foreground uppercase',
                'transition-colors duration-300 hover:text-foreground',
              )}
            >
              {name}
            </span>
          ))}
        </Marquee>
      </div>
    </section>
  );
}
